import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { RichBlogPost, RichBlogSummary } from "./rich-types";

function publicClient() {
  return createClient(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

function validate(s: Partial<RichBlogPost>): asserts s is RichBlogPost {
  const req = ["slug", "title", "description", "date", "readMinutes", "keywords", "sections"] as const;
  for (const k of req) {
    if ((s as Record<string, unknown>)[k] === undefined) {
      throw new Error(`Missing required field: ${k}`);
    }
  }
  if (!/^[a-z0-9-]+$/.test(s.slug as string)) {
    throw new Error("slug must be kebab-case (a-z, 0-9, hyphen only)");
  }
  if (!Array.isArray(s.sections) || s.sections.length === 0) {
    throw new Error("sections must be a non-empty array");
  }
  if (!Array.isArray(s.keywords)) throw new Error("keywords must be an array");
  if ((s.title as string).length > 75) throw new Error("title should be ≤ 75 characters for SEO");
  if ((s.description as string).length > 200) throw new Error("description should be ≤ 200 characters");
}

/** Public: list every published custom blog post. */
export const listCustomBlogPosts = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ items: RichBlogSummary[] }> => {
    const sb = publicClient();
    const { data, error } = await sb
      .from("custom_blog_posts")
      .select("slug, spec")
      .eq("is_published", true);
    if (error || !data) return { items: [] };
    return {
      items: data.map((r) => {
        const p = r.spec as RichBlogPost;
        return {
          slug: p.slug,
          title: p.title,
          description: p.description,
          date: p.date,
          category: p.category,
          readMinutes: p.readMinutes,
        };
      }),
    };
  },
);

/** Public: fetch one custom post by slug. */
export const getCustomBlogPost = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data }): Promise<{ post: RichBlogPost | null }> => {
    const sb = publicClient();
    const { data: row, error } = await sb
      .from("custom_blog_posts")
      .select("spec")
      .eq("slug", data.slug)
      .eq("is_published", true)
      .maybeSingle();
    if (error || !row) return { post: null };
    return { post: row.spec as RichBlogPost };
  });

/** Admin: list all posts (published + draft). */
export const adminListCustomBlogPosts = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId, _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    const { data, error } = await context.supabase
      .from("custom_blog_posts")
      .select("slug, spec, is_published, updated_at")
      .order("updated_at", { ascending: false });
    if (error) throw new Error(error.message);
    return {
      items: (data ?? []).map((r) => ({
        slug: r.slug,
        title: (r.spec as RichBlogPost)?.title ?? r.slug,
        is_published: r.is_published,
        updated_at: r.updated_at,
      })),
    };
  });

export const adminUpsertCustomBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { spec: Partial<RichBlogPost>; is_published?: boolean }) => d)
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId, _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    validate(data.spec);
    const spec = data.spec as RichBlogPost;
    const published = data.is_published ?? true;

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("custom_blog_posts").upsert(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      { slug: spec.slug, spec: spec as any, is_published: published, created_by: context.userId },
      { onConflict: "slug" },
    );
    if (error) throw new Error(error.message);

    if (published) {
      const url = `https://drhint.com/blog/${spec.slug}`;
      try {
        const { broadcastIndexNow } = await import("@/lib/indexnow");
        await broadcastIndexNow([url]);
      } catch (err) {
        console.error("[blog] indexnow failed:", (err as Error).message);
      }
      try {
        await supabaseAdmin.from("indexing_queue").upsert(
          { url, source: "admin-blog", priority: 15, bing_submitted: true, bing_last_submitted: new Date().toISOString() },
          { onConflict: "url" },
        );
      } catch (err) {
        console.error("[blog] indexing_queue failed:", (err as Error).message);
      }
    }
    return { ok: true, url: `/blog/${spec.slug}` };
  });

export const adminDeleteCustomBlogPost = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data, context }) => {
    const { data: isAdmin } = await context.supabase.rpc("has_role", {
      _user_id: context.userId, _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin.from("custom_blog_posts").delete().eq("slug", data.slug);
    if (error) throw new Error(error.message);
    return { ok: true };
  });